import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Loader2, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Panel } from "@/components/common";
import { api, formatApiError } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";

const Photo = ({ p, onDelete, onOpen }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let u;
    api.get(`/photos/${p.id}`, { responseType: "blob" }).then((r) => { u = URL.createObjectURL(r.data); setUrl(u); }).catch(() => {});
    return () => u && URL.revokeObjectURL(u);
  }, [p.id]);
  return (
    <figure className="group relative overflow-hidden rounded-lg border border-slate-200 bg-slate-100" data-testid={`photo-${p.id}`}>
      <button onClick={() => url && onOpen(url)} className="block aspect-square w-full">
        {url ? <img src={url} alt={p.caption || p.original_filename} className="h-full w-full object-cover" /> : <Loader2 className="m-auto mt-[40%] h-5 w-5 animate-spin text-slate-400" />}
      </button>
      <figcaption className="truncate px-2 py-1 text-[11px] text-slate-600" title={p.caption}>{p.caption || p.original_filename} · {p.uploaded_by}</figcaption>
      <button onClick={() => onDelete(p)} className="absolute right-1 top-1 rounded bg-white/90 p-1 text-rose-600 opacity-0 transition-opacity group-hover:opacity-100" data-testid={`photo-delete-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
    </figure>
  );
};

export const PhotosPanel = ({ booking }) => {
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [caption, setCaption] = useState("");
  const [open, setOpen] = useState(null);
  const input = useRef();

  const load = () => api.get(`/bookings/${booking.id}/photos`).then((r) => setPhotos(r.data)).catch(() => {});
  useEffect(() => { load(); }, [booking.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        await api.post(`/bookings/${booking.id}/photos?caption=${encodeURIComponent(caption)}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(`${files.length} photo${files.length > 1 ? "s" : ""} uploaded`);
      setCaption("");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); if (input.current) input.current.value = ""; }
  };
  const remove = async (p) => {
    if (!window.confirm("Remove this photo?")) return;
    try { await api.delete(`/photos/${p.id}`); setPhotos((l) => l.filter((x) => x.id !== p.id)); } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Panel title={`Photos (${photos.length})`} action={
      <Button size="sm" variant="outline" disabled={busy} onClick={() => input.current?.click()} data-testid="btn-add-photo">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />} Add photos</Button>}>
      <input ref={input} type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={(e) => upload([...e.target.files])} data-testid="photo-file-input" />
      <input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Optional caption for the next upload, e.g. Damage – fridge door" className="mb-3 h-9 w-full rounded-md border border-slate-200 px-3 text-sm" data-testid="photo-caption-input" />
      {photos.length === 0 ? <p className="text-sm text-slate-500" data-testid="photos-empty">No photos yet. Crews can add inventory or damage photos from their phone.</p> : (
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4" data-testid="photos-grid">{photos.map((p) => <Photo key={p.id} p={p} onDelete={remove} onOpen={setOpen} />)}</div>
      )}
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={() => setOpen(null)} data-testid="photo-lightbox">
          <img src={open} alt="" className="max-h-full max-w-full rounded-lg" />
          <button className="absolute right-4 top-4 rounded-full bg-white/20 p-2 text-white"><X className="h-5 w-5" /></button>
        </div>
      )}
    </Panel>
  );
};
